import { useEffect, useRef, useState } from "react";
import { Check, Layers } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { MAP_STYLES, type MapStyle } from "@/lib/map-styles";
import { cn } from "@/lib/utils";

interface MapStylePickerProps {
  currentStyle: MapStyle;
  onStyleChange: (style: MapStyle) => void;
  align?: "start" | "end";
  container?: HTMLElement | null;
  buttonVariant?: "outline" | "secondary" | "ghost";
  buttonSize?: "sm" | "icon";
  className?: string;
}

export function MapStylePicker({
  currentStyle,
  onStyleChange,
  align = "start",
  buttonVariant = "secondary",
  buttonSize = "sm",
  className = "gap-2 shadow-md",
}: MapStylePickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <Button
        type="button"
        variant={buttonVariant}
        size={buttonSize}
        className={className}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <Layers className="h-4 w-4" />
        {buttonSize !== "icon" && t(currentStyle.labelKey)}
      </Button>
      {open && (
        <div
          role="menu"
          className={cn(
            "absolute top-full z-50 mt-1 min-w-40 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md",
            align === "end" ? "right-0" : "left-0"
          )}
        >
        {MAP_STYLES.map((style) => (
          <button
            key={style.id}
            type="button"
            role="menuitem"
            onClick={() => {
              onStyleChange(style);
              setOpen(false);
            }}
            className={cn(
              "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
              currentStyle.id === style.id && "bg-accent"
            )}
          >
            <Check className={cn("h-4 w-4", currentStyle.id !== style.id && "invisible")} />
            {t(style.labelKey)}
          </button>
        ))}
        </div>
      )}
    </div>
  );
}
